import type { CollectibleId } from "@/services/collectibles/collectiblesTypes";
import type { CategoryId } from "@/services/collection/categories/categoriesSchema";
import { createCollectionItem, updateCollectionItem } from "@/services/collection/item/collectionItemApi";
import type { CollectionItem } from "@/services/collection/item/collectionItemTypes";
import { orderGalleryImages, uploadImage } from "@/services/collection/item/gallery/api";
import { batchSetCollectionItemParameters, type ParameterSetRequest } from "@/services/parameters/parametersApi";
import { withRetry } from "@/utilities/retry";

type Photos = { front: File; back?: File };

export type CreateCollectionItemFromCollectibleOptions = {
  collectibleId: CollectibleId;
  categoryId: CategoryId;
  photos?: Photos;
  aiIdentificationSourceId?: string;
  publicNote?: string;
  primaryParams?: ParameterSetRequest[];
};

export const createCollectionItemFromCollectible = async (options: CreateCollectionItemFromCollectibleOptions): Promise<CollectionItem> => {
  const item = await withRetry(() =>
    createCollectionItem({
      categoryId: options.categoryId,
      collectibleId: options.collectibleId,
      set: options.collectibleId,
      minifigure: options.collectibleId,
      userCollectionListName: "",
    })
  );

  const tasks: Promise<unknown>[] = [];
  let frontImageId: number | undefined;
  let backImageId: number | undefined;

  if (options.photos) {
    const photos = options.photos;
    const backFile = photos.back;
    tasks.push(
      Promise.all([withRetry(() => uploadImage(photos.front, item, { suppressToast: true })), backFile ? withRetry(() => uploadImage(backFile, item, { suppressToast: true })) : undefined]).then(([front, back]) => {
        if (front.status === "ok") frontImageId = front.imageId;
        if (back && back.status === "ok") backImageId = back.imageId;
      })
    );
  }

  const aiIdentificationSourceId = options.aiIdentificationSourceId;
  if (aiIdentificationSourceId !== undefined) {
    tasks.push(withRetry(() => updateCollectionItem(item.id, { aiIdentificationSourceId })));
  }

  const publicNote = options.publicNote;
  if (publicNote !== undefined) {
    tasks.push(withRetry(() => updateCollectionItem(item.id, { publicNote })));
  }

  const primaryParams = options.primaryParams;
  if (primaryParams && primaryParams.length > 0) {
    tasks.push(withRetry(() => batchSetCollectionItemParameters(item.id, primaryParams)));
  }

  await Promise.all(tasks);

  // Photo uploads (and other creation tasks, e.g. aiIdentificationSourceId, which may attach
  // additional images server-side) run in parallel, so the gallery order they land in is
  // unpredictable. Force front/back to the top, in order, once everything above has settled.
  if (frontImageId !== undefined) {
    const orderedImageIds = backImageId !== undefined ? [frontImageId, backImageId] : [frontImageId];
    await withRetry(() => orderGalleryImages(orderedImageIds));
  }

  return item;
};
